import {
  QTopPolicyViolationsCard,
  QAppVersionsWithMostPolicyViolationsCard,
  QAppsCountCard,
  QAppVersionPolicyViolationsOverTimeCard,
} from '@kw/quokka-ui';

import { memo, useMemo } from 'react';
import type { ComponentType } from 'react';
import { Box } from '@mui/material';
import { DashboardAnalyticsDateRangeDays, DashboardAnalyticsMetrics } from './dashboardAnalytics';
import { useDashboardNavigation } from './useDashboardNavigation';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useFetchService } from '../../utils/fetchService';
import type { PolicyEntity } from '../Policy/getData';
import type { TopPolicyViolationItem } from '@kw/quokka-ui';

type PolicyViolationsProps = {
  dateRangeDays: DashboardAnalyticsDateRangeDays;
  metrics: DashboardAnalyticsMetrics | null | undefined;
};

type PolicyListResponse = {
  results: Pick<PolicyEntity, 'id' | 'name' | 'enabled'>[];
};

const platforms = ['android', 'ios'] as const;

const TopPolicyViolationsCard = QTopPolicyViolationsCard as ComponentType<{
  platform: (typeof platforms)[number];
  data: unknown[];
  getPolicyNameHref?: (item: TopPolicyViolationItem) => string;
}>;

const summaryCardCellSx = {
  gridColumn: 'span 2',
  '& .MuiPaper-root': { height: '100%' },
};

const TopPolicyViolationsRow = memo(({ dateRangeDays, metrics }: PolicyViolationsProps) => {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const { getApplicationsHref } = useDashboardNavigation(dateRangeDays);
  const { data: policiesData } = useFetchService('/eval/policies/list', accessToken, Boolean(accessToken)) as {
    data?: PolicyListResponse;
  };

  const policyNameToId = useMemo(
    () =>
      (policiesData?.results ?? []).reduce<Record<string, string>>((acc, policy) => {
        acc[policy.name] = policy.id;
        return acc;
      }, {}),
    [policiesData],
  );

  const getPolicyNameHref = (platform: (typeof platforms)[number], item: TopPolicyViolationItem) => {
    const policyName = item.policyName;
    const policyId = policyNameToId[policyName];

    return getApplicationsHref({ platform, policyViolations: policyId });
  };

  return (
    <>
      {platforms.map(platform => (
        <Box key={`top-policy-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <TopPolicyViolationsCard
            platform={platform}
            data={(platform === 'android' ? metrics?.topPolicyViolationsAndroid : metrics?.topPolicyViolationsIos) ?? []}
            {...(policiesData ? { getPolicyNameHref: item => getPolicyNameHref(platform, item) } : {})}
          />
        </Box>
      ))}
    </>
  );
});

export const PolicyViolations = ({ dateRangeDays, metrics }: PolicyViolationsProps) => {
  const { getAppReportHref, getApplicationsHref } = useDashboardNavigation(dateRangeDays);
  const { versionCount, androidVersionCount, iosVersionCount, uniqueVersionCount, uniqueAndroidVersionCount, uniqueIosVersionCount } =
    metrics?.policyViolationsVersionCount ?? {};

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '24px',
        '& .MuiPaper-root': { width: '100%' },
      }}
    >
      {/* Row 1 — policy violations over time */}
      <Box key="policy-violations-over-time" sx={{ gridColumn: 'span 6' }}>
        <QAppVersionPolicyViolationsOverTimeCard data={metrics?.policyViolationsOverTime ?? []} />
      </Box>

      {/* Row 2 — count for total, android, and ios app versions with violations */}
      <Box sx={summaryCardCellSx}>
        <QAppsCountCard
          data-testid="total-versions-with-violations-card"
          variant="totalVersionsWithViolations"
          versionsCount={versionCount}
          uniqueAppsCount={uniqueVersionCount}
          emptyStateMessage="Versions with Policy Violations not available"
          href={getApplicationsHref({ platform: 'all', policyViolations: 'has_violations' })}
        />
      </Box>
      <Box key={'android'} sx={summaryCardCellSx}>
        <QAppsCountCard
          data-testid={`android-versions-with-violations-card`}
          variant={'androidVersionsWithViolations'}
          versionsCount={androidVersionCount}
          uniqueAppsCount={uniqueAndroidVersionCount}
          emptyStateMessage="Android Versions with Policy Violations not available"
          href={getApplicationsHref({ platform: 'android', policyViolations: 'has_violations' })}
        />
      </Box>
      <Box key={'ios'} sx={summaryCardCellSx}>
        <QAppsCountCard
          data-testid={`ios-versions-with-violations-card`}
          variant={'iosVersionsWithViolations'}
          versionsCount={iosVersionCount}
          uniqueAppsCount={uniqueIosVersionCount}
          emptyStateMessage="iOS Versions with Policy Violations not available"
          href={getApplicationsHref({ platform: 'ios', policyViolations: 'has_violations' })}
        />
      </Box>

      {/* Row 3: Top 10 Policy Violations — Android then iOS */}
      <TopPolicyViolationsRow dateRangeDays={dateRangeDays} metrics={metrics} />

      {/* Row 4: App Versions With Most Policy Violations — Android then iOS */}
      {platforms.map(platform => (
        <Box key={`app-versions-policy-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <QAppVersionsWithMostPolicyViolationsCard
            platform={platform}
            data={
              (platform === 'android'
                ? metrics?.appVersionWithMostPolicyViolationsAndroid
                : metrics?.appVersionWithMostPolicyViolationsIos) ?? []
            }
            getViewReportHref={item => getAppReportHref(item.analysisId)}
          />
        </Box>
      ))}
    </Box>
  );
};
